import { Activity, memo, useState } from 'react';

import { Speaker } from '@/types/speakers.types';
import Table from '@layouts/layout-table/layout-table';
import TableBody from '@layouts/layout-table/layout-table-body';
import TableHead from '@layouts/layout-table/layout-table-head';
import TableRow from '@layouts/layout-table/layout-table-row';
import useSortedAndPagedCastList from './helpers/use-sorted-and-paged-cast-list';
import CastContentTableHeader from './cast-content-table-header';
import CastContentTableRow from './cast-content-table-row';
import EditCastMember from './edit-cast-member';
import { selectHomepageAndi18nLanguages, selectWorkingProjectDefaultLanguage } from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';
import useComputedColorVariableValue from '@/utils/colors/use-computed-color-variable-value';
import Button from '@ui/buttons/button/button';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import Text from '@ui/text/text/text';

type TCastListProps = {
  speakers: Speaker[];
  sessionUuid: string;
};

const MAX_ROWS_COLLAPSED = 5;

const CastList = memo(({ speakers, sessionUuid }: TCastListProps) => {
  const { sortedCast } = useSortedAndPagedCastList({ speakers });
  const sortedCastCount = sortedCast?.length || 0;

  const languages = useAppSelector(selectHomepageAndi18nLanguages);

  const _defaultLang = useAppSelector(selectWorkingProjectDefaultLanguage);
  const defaultLang = _defaultLang || 'en';

  const computedBgHex = useComputedColorVariableValue({ variable: 'var(--BG)' });

  const [editSpeaker, setEditSpeaker] = useState<Speaker | null>(null);
  const [showMore, setShowMore] = useState(false);

  return (
    <div>
      <SlideDownCard
        // 64px is each row height, 36px is the table header height
        height={showMore ? undefined : `calc(calc(64px * ${Math.min(MAX_ROWS_COLLAPSED, sortedCastCount)}) + 36px)`}
        open
        autoGrow={showMore}
        wrapperClassname="overflow-hidden"
      >
        <Table ignoreHeaderRowSpacing>
          <TableHead border={0} bgcolor={computedBgHex} className="position-sticky top-0 z-2 height-auto">
            <TableRow border={0} className="height-30">
              <CastContentTableHeader />
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedCast?.map((speaker, idx) => {
              const isInert = !showMore && idx > MAX_ROWS_COLLAPSED - 1;
              return (
                <CastContentTableRow
                  inert={isInert}
                  setEditSpeaker={setEditSpeaker}
                  sessionUuid={sessionUuid}
                  key={speaker.speaker}
                  speaker={speaker}
                />
              );
            })}
          </TableBody>
        </Table>
      </SlideDownCard>

      <Activity mode={sortedCastCount > MAX_ROWS_COLLAPSED ? 'visible' : 'hidden'}>
        <div className={`flex jc-center ai-center ${showMore ? 'margin-t-6' : ''}`}>
          <Button designSize="small" design="LocalBttnSecHalfTransp" onClick={() => setShowMore((prev) => !prev)}>
            <Text className="L2">Show {showMore ? 'Less' : 'More'}</Text>
          </Button>
        </div>
      </Activity>

      <EditCastMember
        editSpeaker={editSpeaker}
        setEditSpeaker={setEditSpeaker}
        defaultLang={defaultLang}
        languages={languages}
      />
    </div>
  );
});

CastList.displayName = 'CastList';

export default CastList;
